import { G } from '@mobily/ts-belt';
import { assign, createMachine, pure } from 'xstate';
import type { ReportSelectEventData } from '@stitch-fix/event-reporter';
import type { PaymentType } from '@stitch-fix/form-payments';
import { flows, type FlowId } from './flows';
import { browserLogger } from '../../../../utils/loggers/browser';
import {
  getSteps,
  persistFastlaneOTPStatus,
  persistStepData,
  type FastlaneOTPStatus,
} from './utils';
import type {
  AnchoredFixScheduleInput,
  BusinessLine,
  FixScheduleInput,
} from '../../../../types/definitions/vendor/graphql-schema.d';

export type ScheduleFixEvent = Extract<
  ReportSelectEventData,
  { schema: 'schedule_fix' }
>;

export type FlowContext = {
  flow: FlowId;
  currentStepIndex: number;
  nextStepPath?: string;
  previousStepPath?: string;
  data?: FlowData;
  errorMessage?: string;
  anchorFixOutOfStock?: boolean;
  navTitle?: string;
  businessLine: BusinessLine;
  isFastlaneEnabled?: boolean;
  fastlaneOTPStatus?: FastlaneOTPStatus;
  isCreditCardOptimizationEnabled?: boolean;
  email?: string;
  paymentType?: PaymentType;
};

export type FlowData = Partial<FixScheduleInput> &
  Partial<AnchoredFixScheduleInput>;

type FlowEvent =
  | { type: 'BACK' }
  | { type: 'NEXT'; data?: FlowData }
  | {
      type: 'RESET_FLOW';
      flow: FlowId;
      stepIndex: number;
      data: FlowData;
      businessLine: BusinessLine;
      isFastlaneEnabled: boolean;
      fastlaneOTPStatus: FastlaneOTPStatus;
      isCreditCardOptimizationEnabled: boolean;
      email: string;
    }
  | { type: 'PAYMENT_ERROR_REDIRECT'; message: string }
  | { type: 'UPDATE_FLOW_STEPS'; currentStepIndex: number }
  | { type: 'UPDATE_NAV_TITLE'; title?: string }
  | { type: 'UPDATE_PAYMENT_TYPE'; paymentType: PaymentType }
  | { type: 'UPDATE_FASTLANE_OTP_STATUS'; fastlaneOTPStatus: FastlaneOTPStatus }
  | { type: 'SET_ANCHOR_FIX_OUT_OF_STOCK' };

type FlowTypestate =
  | {
      value: 'idle' | 'navigating';
      context: FlowContext;
    }
  | {
      value: 'complete';
      context: FlowContext & {
        data: FixScheduleInput | AnchoredFixScheduleInput;
      };
    };

export const flowMachine = createMachine<FlowContext, FlowEvent, FlowTypestate>(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QDMA2B7A7gOgJYVTAGIAhAQQGEBpAbQAYBdRUAB3VlwBdd0A7ZkAA9EAWgBMYgKzYAbAA46kgJwyALAGYxcpWIDsAGhABPUZLrYAjDJm7VY1arkWl6yapkBfD4bRY8BYgA5AFEADQAVeiYkEDYObj4BYQQRVQt1bF06ZwklOjF1C10ZQxMUyTlsJzMdG3Us1V1dSS8fDBx8QiIAJWCAZWDwgH0AMQAZAHkAdSiBOK4efhjk8SULbHU5eWd1GQrGuVLRdXUlWSVJXR2LRSUNXVaQXw6AogBVAAUAETJw4NHJlMhn0-h8+rMYvMEktQCsVJYxEpqhY5FpaodjMdTudLtdbvdHs9-F0Iax2AtEstEFZzA46DItO4xHR1NkjilnGcTnJdijCrZ1A5Ce1sLwAIYAN1wUDF3F4UCIED4YDwvAl6AA1iqieKpTK5VAELg1egAMayxZRUmxcnQpKiKzrRwOPIVVFKZrskQWMTYNb3OSNH0XZlyYV+XXSi3yohgABOcfQcewLFQsuQSYAttgdZKowajSbzdCrYw5rbFvaUnpsJJTkVJD65Ao9Cp2ZIZJYlGssv66I1JB3wzhTehM6mwJxiB8yABNACywUCw2C3W6E26Q16XwAkr0KJEy5CK5TYYgxPJMkzJPZUQ31F76pZVHQ7q+roibxYvN4QLx0BAcACM85bxJWVIpO46xZDkiL5PyJSYpBNbOLiLg3MoBK-kSnRgKBFIwkIxwdrWGj2KoFzdiy7hehIGQ8jy1gyHQWSso4w6inm+rGlA+F2hBIi7JUDhNHQ9IWG4VgPkhzKqNgLqnIKg5ZPknjYSKo7joQU58eBZ4IIK6yNioeyOJoRTSWU4jZLWza7Io+R1i46g-h4QA */
    id: 'flow',
    predictableActionArguments: true,
    initial: 'idle',
    states: {
      idle: {
        description:
          'Await user navigation events, or transition to complete state if final step.',
        always: { target: 'complete', cond: 'isFinalStep' },
        exit: 'clearError',
        on: {
          BACK: 'navigating',
          NEXT: {
            actions: ['assignDataToContext', 'updateCookie'],
            target: 'navigating',
          },
          RESET_FLOW: { actions: 'resetFlow' },
          UPDATE_FLOW_STEPS: { actions: 'assignStepsToContext' },
          UPDATE_NAV_TITLE: { actions: 'assignNavTitleToContext' },
          UPDATE_PAYMENT_TYPE: { actions: 'assignPaymentTypeToContext' },
          UPDATE_FASTLANE_OTP_STATUS: {
            actions: [
              'assignFastlaneOTPStatusToContext',
              'updateFastlaneOTPCookie',
            ],
          },
        },
      },

      navigating: {
        description: `Invoke the handleFlowNavigation service, which is defined as a config
          option in FlowProvider, and handles step transition animations and routing.`,
        invoke: {
          src: 'handleFlowNavigation',
          onDone: {
            target: 'idle',
          },
          onError: {
            target: 'idle',
            actions: 'logError',
          },
        },
      },

      complete: {
        on: {
          SET_ANCHOR_FIX_OUT_OF_STOCK: {
            target: 'navigating',
            actions: [
              'assignErrorMessageToContext',
              'assignPreviousStepAfterError',
            ],
          },
          PAYMENT_ERROR_REDIRECT: {
            target: 'navigating',
            actions: [
              'assignErrorMessageToContext',
              'assignPreviousStepAfterError',
            ],
          },
        },
      },
    },
  },
  {
    guards: {
      isFinalStep: ctx => {
        return (
          !G.isUndefined(ctx.flow) &&
          ctx.currentStepIndex === flows[ctx.flow].length - 1
        );
      },
    },
    actions: {
      assignDataToContext: assign({
        data: (ctx, ev) => ({
          ...ctx.data,
          ...(ev.type === 'NEXT' && ev.data),
        }),
      }),
      assignErrorMessageToContext: assign({
        errorMessage: (_, ev) =>
          ev.type === 'PAYMENT_ERROR_REDIRECT' ? ev.message : undefined,
        anchorFixOutOfStock: (_, ev) =>
          ev.type === 'SET_ANCHOR_FIX_OUT_OF_STOCK',
      }),
      assignPreviousStepAfterError: assign((ctx, event) => {
        // We need to set the context to the previous step when the user encounters a payment error
        if (
          event.type === 'PAYMENT_ERROR_REDIRECT' ||
          event.type === 'SET_ANCHOR_FIX_OUT_OF_STOCK'
        ) {
          const previousStepIndex = Math.max(ctx.currentStepIndex - 1, 0);

          return {
            ...ctx,
            ...getSteps({
              flow: ctx.flow,
              currentStepIndex: previousStepIndex,
              businessLine: ctx.businessLine,
            }),
          };
        }

        return ctx;
      }),
      assignStepsToContext: assign((ctx, ev) => {
        if (ev.type === 'UPDATE_FLOW_STEPS') {
          return {
            ...ctx,
            ...getSteps({
              flow: ctx.flow,
              currentStepIndex: ev.currentStepIndex,
              businessLine: ctx.businessLine,
            }),
          };
        }

        return ctx;
      }),
      assignNavTitleToContext: assign({
        navTitle: (_, ev) =>
          ev.type === 'UPDATE_NAV_TITLE' ? ev.title : undefined,
      }),
      assignPaymentTypeToContext: assign({
        paymentType: (_, ev) =>
          ev.type === 'UPDATE_PAYMENT_TYPE' ? ev.paymentType : undefined,
      }),
      assignFastlaneOTPStatusToContext: assign({
        fastlaneOTPStatus: (_, ev) =>
          ev.type === 'UPDATE_FASTLANE_OTP_STATUS'
            ? ev.fastlaneOTPStatus
            : undefined,
      }),
      clearError: pure(
        ctx =>
          ctx.errorMessage &&
          assign({
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            errorMessage: _ => undefined,
          }),
      ),
      logError: (error: unknown) =>
        browserLogger({
          level: 'error',
          feature: 'lower-fix-funnel',
          message: 'Lower Fix Funnel navigation error',
          context: { error },
        }),
      resetFlow: assign((ctx, ev) => {
        if (ev.type === 'RESET_FLOW') {
          const {
            flow,
            stepIndex,
            data,
            businessLine,
            isFastlaneEnabled,
            fastlaneOTPStatus,
            isCreditCardOptimizationEnabled,
            email,
          } = ev;

          return {
            flow,
            data,
            businessLine,
            isFastlaneEnabled,
            fastlaneOTPStatus,
            isCreditCardOptimizationEnabled,
            email,
            ...getSteps({ flow, currentStepIndex: stepIndex, businessLine }),
          };
        }

        return ctx;
      }),
      updateCookie: context => {
        persistStepData(context.data);
      },
      updateFastlaneOTPCookie: context => {
        persistFastlaneOTPStatus(context.fastlaneOTPStatus);
      },
    },
  },
);
